/**
 * ModelFilterModal Component
 *
 * Centered modal for model filtering in collapsed sidebar.
 * Uses Headless UI Dialog with smooth fade/scale animations.
 */

import React, { Fragment } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition } from '@headlessui/react';
import { ModelFilter, type ModelFilterProps } from './ModelFilter';

interface ModelFilterModalProps extends ModelFilterProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ModelFilterModal: React.FC<ModelFilterModalProps> = ({
  isOpen,
  onClose,
  responses,
  modelDetails,
  selectedModels,
  filterMode,
  onFilterChange
}) => {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-[100]"
    >
      {/* Backdrop with fade animation */}
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <DialogBackdrop className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
      </Transition.Child>

      {/* Modal container - bottom anchored on mobile for viewport safety */}
      <div className="fixed inset-0 flex items-end md:items-center justify-center p-4 md:p-6 pointer-events-none">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel
            className="w-full max-w-lg md:max-w-xl bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-2xl pointer-events-auto relative flex flex-col max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] overflow-hidden"
            data-testid="model-filter-modal"
          >
            {/* Accessibility title (visually hidden) */}
            <DialogTitle className="sr-only">Filter by Model</DialogTitle>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close model filter"
              className="inline-flex absolute top-3 right-3 h-9 w-9 items-center justify-center rounded-full text-slate-300 hover:text-white hover:bg-slate-800/80 focus:outline-none focus:ring-2 focus:ring-cyan-400"
            >
              ×
            </button>

            {/* Modal content - padding for proper spacing */}
            <div
              className="p-4 space-y-4 w-full overflow-y-auto"
              style={{
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)'
              }}
            >
              {/* Educational description */}
              <div className="rounded-xl border border-slate-700/70 bg-slate-800/60 px-4 py-3 space-y-2">
                <p className="text-sm font-medium text-slate-100">Pick models to narrow comparisons.</p>

                <div className="space-y-2 text-sm text-slate-300">
                  <div className="flex gap-3 items-start">
                    <span className="inline-flex items-center rounded-full border border-cyan-400/40 bg-cyan-500/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-cyan-200">
                      Any
                    </span>
                    <p className="leading-relaxed">Shows comparisons containing at least one selected model.</p>
                  </div>

                  <div className="flex gap-3 items-start">
                    <span className="inline-flex items-center rounded-full border border-cyan-400/40 bg-cyan-500/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-cyan-200">
                      All
                    </span>
                    <p className="leading-relaxed">Shows comparisons that include every selected model.</p>
                  </div>
                </div>
              </div>

              <ModelFilter
                responses={responses}
                modelDetails={modelDetails}
                selectedModels={selectedModels}
                filterMode={filterMode}
                onFilterChange={onFilterChange}
              />
            </div>
          </DialogPanel>
        </Transition.Child>
      </div>
    </Dialog>
  );
};
